import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsMissionControlHomepageAndBetaFeatureFlagOn,
} from 'core/userContext';
import { Journey } from 'modules/domains/campaign';
import {
  INHERITED_CONVERSION_ATTRIBUTION_TOOLTIPS_BY_SOURCE,
  InheritedConversionSource,
} from './useInheritedConversionInfo.constants';

interface UseInheritedConversionInfoParams {
  projectGoals?: Journey['goals'];
  journeyGoals?: Journey['goals'];
  source: InheritedConversionSource;
}

export const useInheritedConversionInfo = ({
  projectGoals = [],
  journeyGoals = [],
  source,
}: UseInheritedConversionInfoParams) => {
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isMissionControlAndBetaEnabled = useSelector(
    selectIsMissionControlHomepageAndBetaFeatureFlagOn,
  );

  const currentJourneyGoal = journeyGoals?.find((x) => x.isActive);
  const journeyConversion = currentJourneyGoal?.conversions?.[0]; // There should only be one active conversion event

  const journeyEventName = journeyConversion?.eventName;
  const hasJourneyConversion = !!journeyEventName;

  const projectEventNames = useMemo(
    () =>
      projectGoals?.reduce<string[]>((acc, goal) => {
        if (!goal?.isActive) return acc;

        const eventNames =
          goal.conversions
            ?.map((conversion) => conversion?.eventName)
            .filter((eventName): eventName is string => !!eventName) ?? [];

        return [...acc, ...eventNames];
      }, []) ?? [],
    [projectGoals],
  );

  const hasProjectConversion = projectEventNames?.length > 0;

  const isJourneyAttributionModesDisabled = isJourneyGoalsEnabled && !!hasJourneyConversion;
  const isProjectAttributionModesDisabled =
    isMissionControlAndBetaEnabled && !!hasProjectConversion;

  const isAttributionModesDisabled =
    isJourneyAttributionModesDisabled || isProjectAttributionModesDisabled;

  const attributionModesDisabledTooltipContent = match({
    isJourneyAttributionModesDisabled,
    isProjectAttributionModesDisabled,
  })
    .with(
      { isProjectAttributionModesDisabled: true },
      () => INHERITED_CONVERSION_ATTRIBUTION_TOOLTIPS_BY_SOURCE[source].projectGoalsAttribution,
    )
    .with(
      { isJourneyAttributionModesDisabled: true },
      () => INHERITED_CONVERSION_ATTRIBUTION_TOOLTIPS_BY_SOURCE[source].journeyGoalsAttribution,
    )
    .otherwise(() => undefined);

  return {
    journeyEventName,
    projectEventNames,
    hasProjectConversion,
    hasJourneyConversion,
    isAttributionModesDisabled,
    attributionModesDisabledTooltipContent,
  };
};
